import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, XCircle, MapPin, Navigation2, Ruler, Clock, Zap, Receipt, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatMoney } from "../lib/money";
import SaveHomeWizard from "../components/SaveHomeWizard";
import confetti from "canvas-confetti";

// ─────────────────────────────────────────────────────────────
// PaymentReceipt — reusable rich success card with fare breakdown,
// pickup/dropoff summary, PayPal capture id, and a share-copy.
// ─────────────────────────────────────────────────────────────
function PaymentReceipt({ order, captureId, provider }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);

  const currency = order?.currency || "USD";
  const total = order?.total ?? 0;
  const br = order?.fare_breakdown || {};
  const kmCost = br.km_cost;
  const minuteCost = br.minute_cost;
  const baseFare = br.base_fare;
  const distanceKm = br.distance_km ?? order?.distance_km;
  const durationMin = br.duration_min ?? order?.duration_min;
  const preSurge = br.pre_surge_estimate;
  const surgeMult = br.surge_multiplier;

  const copyCapture = () => {
    if (!captureId) return;
    navigator.clipboard.writeText(captureId);
    setCopied(true);
    toast.success(t("payment.receipt.captureCopied", "Transaction ID copied"));
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10" data-testid="payment-receipt">
      <div className="bg-ink-surface border border-ink-border rounded-3xl overflow-hidden">
        {/* Success banner */}
        <div className="relative p-6 pb-5 bg-gradient-to-b from-emerald-500/25 to-transparent text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-400 text-obsidian flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30" data-testid="receipt-check">
            <CheckCircle2 size={32} strokeWidth={2.5} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-black text-emerald-300 mt-3">
            {t("payment.receipt.eyebrow", "Payment received")}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1" data-testid="receipt-total">
            {formatMoney(total, currency)}
          </h1>
          <div className="text-xs text-white/60 mt-1">
            {t("payment.receipt.thanks", "Thanks — your driver is set.")}
          </div>
        </div>

        {/* Trip route */}
        {order?.pickup_address && (
          <div className="p-5 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-3">
              {t("payment.receipt.tripSection", "Your trip")}
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <MapPin size={14} className="mt-0.5 text-emerald-300 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-black text-white/40">{t("payment.receipt.pickup", "Pickup")}</div>
                  <div className="text-sm text-white truncate" data-testid="receipt-pickup">{order.pickup_address}</div>
                </div>
              </div>
              {order.dropoff_address && (
                <div className="flex items-start gap-3">
                  <Navigation2 size={14} className="mt-0.5 text-electric-teal shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase font-black text-white/40">{t("payment.receipt.dropoff", "Drop-off")}</div>
                    <div className="text-sm text-white truncate" data-testid="receipt-dropoff">{order.dropoff_address}</div>
                  </div>
                </div>
              )}
            </div>

            {(distanceKm != null || durationMin != null) && (
              <div className="mt-3 flex items-center gap-3 text-[11px] text-white/60">
                {distanceKm != null && (
                  <span className="inline-flex items-center gap-1" data-testid="receipt-distance">
                    <Ruler size={11} /> {Number(distanceKm).toFixed(1)} km
                  </span>
                )}
                {durationMin != null && (
                  <span className="inline-flex items-center gap-1" data-testid="receipt-duration">
                    <Clock size={11} /> {Math.round(durationMin)} min
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Save-Home prompt — surfaces only after this dropoff has been used
            3+ times AND no Home favorite exists there yet. Silent otherwise. */}
        {order?.dropoff_address && order?.dropoff_lat && order?.dropoff_lng && (
          <div className="px-5 pb-1">
            <SaveHomeWizard
              dropoffAddress={order.dropoff_address}
              dropoffLat={order.dropoff_lat}
              dropoffLng={order.dropoff_lng}
            />
          </div>
        )}

        {/* Fare breakdown */}
        {(baseFare != null || kmCost != null || minuteCost != null) && (
          <div className="p-5 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-3 inline-flex items-center gap-1.5">
              <Receipt size={11} /> {t("payment.receipt.breakdownSection", "Fare breakdown")}
            </div>
            <div className="space-y-1.5 text-sm">
              {baseFare != null && (
                <BreakdownRow label={t("payment.receipt.baseFare", "Base fare")} value={formatMoney(baseFare, currency)} testid="receipt-line-base" />
              )}
              {kmCost != null && kmCost > 0 && (
                <BreakdownRow
                  label={`${t("payment.receipt.distanceCharge", "Distance")}${distanceKm ? ` · ${Number(distanceKm).toFixed(1)} km` : ""}`}
                  value={formatMoney(kmCost, currency)}
                  testid="receipt-line-km"
                />
              )}
              {minuteCost != null && minuteCost > 0 && (
                <BreakdownRow
                  label={`${t("payment.receipt.timeCharge", "Time")}${durationMin ? ` · ${Math.round(durationMin)} min` : ""}`}
                  value={formatMoney(minuteCost, currency)}
                  testid="receipt-line-min"
                />
              )}
              {preSurge && surgeMult && surgeMult > 1 && (
                <BreakdownRow
                  label={
                    <span className="inline-flex items-center gap-1 text-amber-300">
                      <Zap size={11} /> {t("payment.receipt.surge", "Surge")} · {Number(surgeMult).toFixed(2)}×
                    </span>
                  }
                  value={<span className="text-amber-300">+{formatMoney(total - preSurge, currency)}</span>}
                  testid="receipt-line-surge"
                />
              )}
              <div className="border-t border-white/10 pt-2 mt-2 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest font-black text-white/60">
                  {t("payment.receipt.total", "Total")}
                </span>
                <span className="text-lg font-black text-white" data-testid="receipt-line-total">
                  {formatMoney(total, currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Transaction reference */}
        {captureId && (
          <div className="p-5 border-t border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-2">
              {t("payment.receipt.txSection", "Transaction reference")}
            </div>
            <button
              onClick={copyCapture}
              className="w-full flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 hover:bg-white/[0.06] transition"
              data-testid="receipt-copy-capture"
            >
              <div className="min-w-0 text-left">
                <div className="text-[9px] uppercase font-black text-white/40 tracking-widest">
                  PayPal · {t("payment.receipt.captureId", "Capture ID")}
                </div>
                <div className="text-xs text-white font-mono truncate" data-testid="receipt-capture-id">{captureId}</div>
              </div>
              <span className={`text-[10px] uppercase font-black tracking-widest inline-flex items-center gap-1 shrink-0 ${copied ? "text-emerald-300" : "text-white/50"}`}>
                <Copy size={11} /> {copied ? t("payment.receipt.copied", "Copied") : t("payment.receipt.copy", "Copy")}
              </span>
            </button>
          </div>
        )}

        {/* CTAs */}
        <div className="p-5 border-t border-white/[0.06] bg-white/[0.02] flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => nav("/activity")}
            className="flex-1 rounded-full bg-white text-obsidian px-5 py-3 text-sm font-black hover:bg-white/90 transition"
            data-testid="receipt-view-orders"
          >
            {t("payment.receipt.viewOrders", "View my orders")} →
          </button>
          <Link
            to="/"
            className="flex-1 rounded-full bg-white/[0.06] border border-white/10 text-white px-5 py-3 text-sm font-black text-center hover:bg-white/[0.10] transition"
            data-testid="receipt-book-again"
          >
            {t("payment.receipt.bookAnother", "Book another")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-white/80" data-testid={testid}>
      <span className="text-sm">{label}</span>
      <span className="text-sm font-black text-white">{value}</span>
    </div>
  );
}

function CenteredCard({ children, testid }) {
  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid={testid}>
      <div className="bg-ink-surface border border-ink-border rounded-3xl p-10">{children}</div>
    </div>
  );
}

function fireConfetti() {
  try {
    confetti({ particleCount: 90, spread: 70, origin: { y: 0.35 }, colors: ["#34d399", "#5eead4", "#ffffff"] });
  } catch { /* canvas-confetti not critical */ }
}

// ─────────────────────────────────────────────────────────────
// PayPal return + capture
// ─────────────────────────────────────────────────────────────
export function PayPalReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState("capturing");
  const [msg, setMsg] = useState("");
  const [order, setOrder] = useState(null);
  const [captureId, setCaptureId] = useState(null);

  useEffect(() => {
    if (authLoading) return; // wait for auth state to hydrate
    const token = params.get("token"); // PayPal appends `?token=<orderId>&PayerID=...`
    if (!token) { setStatus("error"); setMsg("Missing PayPal order token"); return; }

    // If the user is not logged into Drivon (e.g. they opened the PayPal
    // approval flow from a fresh browser), save the resume URL and bounce
    // them through /login. After login they'll return here with the same
    // token in the query string and the capture will proceed.
    if (!user) {
      const resumeUrl = `/paypal/return?${params.toString()}`;
      try { sessionStorage.setItem("drivon_pay_resume", resumeUrl); } catch {}
      nav(`/login?next=${encodeURIComponent(resumeUrl)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/payments/paypal/capture/${token}`);
        if (cancelled) return;
        if (data.status === "paid" || data.status === "already_captured") {
          setStatus("paid");
          setCaptureId(data.capture_id || token);
          const oid = params.get("order_id") || data.order_id;
          if (oid) {
            try {
              const { data: ord } = await api.get(`/orders/${oid}`);
              setOrder(ord);
            } catch { /* fall through to bare success */ }
          }
          fireConfetti();
        } else {
          setStatus("failed"); setMsg(data.status || "capture failed");
        }
      } catch (e) {
        if (cancelled) return;
        const httpStatus = e?.response?.status;
        const detail = e?.response?.data?.detail || e.message;
        // If capture 404′d, it might be because the session at return-time
        // does NOT belong to the account that started the payment (Cause B
        // in the diagnostic). Ask the orphan-hint endpoint whether the tx
        // exists under a different owner. If so, bounce the user to /login
        // with a masked hint so they can re-authenticate as the right user.
        if (httpStatus === 404 && /transaction not found/i.test(detail || "")) {
          try {
            const { data: hint } = await api.get(`/payments/paypal/orphan-hint/${token}`);
            if (hint?.exists && hint?.already_paid) {
              setStatus("paid");
              setCaptureId(token);
              fireConfetti();
              return;
            }
            if (hint?.exists && hint?.owner_email_hint) {
              setStatus("wrong_user");
              setMsg(hint.owner_email_hint);
              return;
            }
          } catch { /* fall through to generic error */ }
        }
        setStatus("failed"); setMsg(detail);
      }
    })();
    return () => { cancelled = true; };
  }, [params, user, authLoading, nav]);

  if (status === "paid") return <PaymentReceipt order={order} captureId={captureId} provider="paypal" />;
  if (status === "capturing") {
    return (
      <CenteredCard testid="paypal-return-page">
        <Loader2 className="animate-spin text-white mx-auto" size={44} />
        <div className="text-xl font-black mt-4">{t("payment.paypal.confirming", "Confirming your PayPal payment…")}</div>
      </CenteredCard>
    );
  }
  if (status === "wrong_user") {
    const resumeUrl = `/paypal/return?${params.toString()}`;
    const loginHref = `/login?next=${encodeURIComponent(resumeUrl)}&hint=${encodeURIComponent(msg)}`;
    return (
      <CenteredCard testid="paypal-return-wrong-user">
        <div className="w-16 h-16 rounded-full bg-brand-warn/20 text-brand-warn flex items-center justify-center mx-auto"><XCircle size={40} /></div>
        <div className="text-2xl font-black mt-4">
          {t("payment.paypal.wrongUser", "Sign in to complete this payment")}
        </div>
        <p className="text-sm text-ink-textDim mt-2">
          {t("payment.paypal.wrongUserDesc",
             "This payment was started from a different account:")} <b>{msg}</b>
        </p>
        <p className="text-xs text-ink-textDim mt-1">
          {t("payment.paypal.wrongUserSafety",
             "Your PayPal approval is safe. No funds have moved. Sign in with the account above to finish.")}
        </p>
        <button
          data-testid="paypal-return-signin-original"
          onClick={() => {
            try { localStorage.removeItem("haulhub_token"); } catch {}
            nav(loginHref, { replace: true });
          }}
          className="mt-6 bg-white text-black rounded-full px-6 py-3 text-sm font-black">
          {t("payment.paypal.signInAsOriginal", "Sign in as {{email}}", { email: msg })}
        </button>
      </CenteredCard>
    );
  }
  return (
    <CenteredCard testid="paypal-return-page">
      <div className="w-16 h-16 rounded-full bg-brand-danger text-white flex items-center justify-center mx-auto"><XCircle size={40} /></div>
      <div className="text-2xl font-black mt-4">{t("payment.paypal.failed", "Payment failed")}</div>
      <p className="text-sm text-ink-textDim mt-2 break-words">{msg}</p>
      <button onClick={() => nav("/services")} className="mt-6 bg-white text-black rounded-full px-6 py-3 text-sm font-black">{t("payment.paypal.retry", "Try again")}</button>
    </CenteredCard>
  );
}
